import React, { useCallback } from 'react';
import {
  BillingDisputesTab,
  BillingHeader,
  BillingHistoryTab,
  BillingHoursTab,
  BillingSummaryTab,
} from '@pay-bill/components';
import {
  AppContent,
  ErrorHandler,
  LoadingState,
  Tabs,
} from '@common/components';
import { useInvBreadcrumbsEffect } from '@pay-bill/hooks';
import { useQuery, useNavigate, useParams } from '@common/hooks';
import {
  InvoiceReviewQuery,
  InvoiceReviewQueryArgs,
  InvoiceReviewQueryResponse,
} from '@pay-bill/graphql';

export const InvoicePage = () => {
  const navigate = useNavigate();
  const { id = '', tabKey = 'summary' } = useParams<{
    id: string;
    tabKey: 'summary' | 'hours' | 'disputes' | 'history';
  }>();

  const { data, loading, error } = useQuery<
    InvoiceReviewQueryResponse,
    InvoiceReviewQueryArgs
  >(InvoiceReviewQuery, {
    variables: {
      id,
    },
  });

  const handleTabChange = useCallback(
    (tabKey: string) => {
      navigate(`/works/billing/invoices/${id}/${tabKey}`);
    },
    [navigate, id]
  );

  const invoice = data?.worksInvoice;

  useInvBreadcrumbsEffect(invoice);

  if (error) {
    return <ErrorHandler error={error} log="InvoicePage" />;
  }

  if (loading || !invoice) {
    return <LoadingState />;
  }

  return (
    <AppContent>
      <BillingHeader invoiceData={invoice} isSupplier={false} />
      <Tabs
        activeKey={tabKey}
        defaultActiveKey={tabKey}
        onChange={handleTabChange}
      >
        <Tabs.Pane tab="Summary" key="summary">
          <BillingSummaryTab invoiceData={invoice} isSupplier={false} />
        </Tabs.Pane>
        <Tabs.Pane tab="Hours" key="hours">
          <BillingHoursTab id={id} isSupplier={false} />
        </Tabs.Pane>
        <Tabs.Pane tab="Disputes" key="disputes">
          <BillingDisputesTab isSupplier={false} invoiceCode={invoice.code} />
        </Tabs.Pane>
        <Tabs.Pane tab="History" key="history">
          <BillingHistoryTab id={id} isSupplier={false} />
        </Tabs.Pane>
      </Tabs>
    </AppContent>
  );
};
